// Progresso's numbers, worked out on the device from the logged sessions (GET_WORKOUT_LOGS): each session's
// exercises with their sets, every exercise's history with its best set, estimated 1RM and trend, and the weeks
// the feed is cut into. Records themselves come from records.js, the same rule the end of a session uses.
import { computeSessionVolume, getWeekBounds, isWarmupEntry } from './workoutStats';
import { epley1RM, getBaseName, readSet, toKg } from './records';

const DAY_MS = 86400000;

export const logDate = (log) => new Date(log?.attributes?.completedAt);

// A session is saved when it ends; it started that long before
export const sessionStart = (log) => new Date(logDate(log).getTime() - (log?.attributes?.duration || 0) * 1000);

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Calendar days from b to a (positive when a is later)
export const daysBetween = (a, b) => Math.round((startOfDay(a) - startOfDay(b)) / DAY_MS);

const heavier = (a, b) => toKg(a.weight, a.unit) > toKg(b.weight, b.unit);

/**
 * The best set as Hevy shows it: the heaviest working set, the most reps at that load; with no load at all, the
 * most reps. Warm-ups only count when there's nothing else.
 */
export const bestSet = (sets) => {
  const all = sets || [];
  if (!all.length) return null;
  const working = all.filter((set) => !set.isWarmup);
  const pool = working.length ? working : all;
  const isLoaded = pool.some((set) => set.weight > 0);
  return pool.reduce((top, set) => {
    if (!isLoaded) return set.reps > top.reps ? set : top;
    if (heavier(set, top)) return set;
    return toKg(set.weight, set.unit) === toKg(top.weight, top.unit) && set.reps > top.reps ? set : top;
  }, pool[0]);
};

/**
 * A session's exercises as the person did them: consecutive entries of one movement (its warm-ups and feeders,
 * then the working sets) become one exercise with all its sets in order.
 */
export const groupSessionExercises = (exercises) => {
  const groups = [];
  (exercises || []).forEach((exercise) => {
    const isWarmup = isWarmupEntry(exercise);
    const name = getBaseName(exercise.name);
    const sets = (exercise.sets || []).map((set) => ({ ...readSet(set), isWarmup }));
    const last = groups[groups.length - 1];
    if (last && last.name === name) {
      last.sets.push(...sets);
      last.thumbnailUrl = last.thumbnailUrl || exercise.thumbnailUrl || null;
    } else {
      groups.push({ name, thumbnailUrl: exercise.thumbnailUrl || null, sets });
    }
  });
  return groups.map((group) => {
    const first = group.sets[0];
    return {
      ...group,
      workingSets: group.sets.filter((set) => !set.isWarmup),
      // The sets a weight record and a 1RM record point at (records.js compares every set, warm-ups included)
      heaviest: group.sets.reduce((top, set) => (heavier(set, top) ? set : top), first),
      top1RM: group.sets.reduce(
        (top, set) => (epley1RM(toKg(set.weight, set.unit), set.reps) > epley1RM(toKg(top.weight, top.unit), top.reps) ? set : top),
        first
      ),
      isLoaded: group.sets.some((set) => set.weight > 0),
      best: bestSet(group.sets),
    };
  });
};

/**
 * Which record a set made, if any: the weight record goes on the heaviest set, the 1RM record on the set with the
 * best estimate. One mark per set; weight wins when both land on the same one.
 */
export const recordKindOf = (record, set, group) => {
  if (!record || !set) return null;
  if (record.weight && set === group.heaviest) return 'weight';
  if (record.oneRM && set === group.top1RM) return 'oneRM';
  return null;
};

/**
 * Every exercise ever logged, the ones trained last first, each with its sessions oldest first:
 * [{ name, thumbnailUrl, isLoaded, entries: [{ log, date, group, record, value, oneRM }] }].
 * value is what the trend follows: the best set's load in kg, or its reps for an exercise done without load.
 */
export const buildExerciseHistory = (logs, groupsByLog, recordsByLog) => {
  const byName = {};
  [...(logs || [])]
    .sort((a, b) => logDate(a) - logDate(b))
    .forEach((log) => {
      (groupsByLog[log.id] || []).forEach((group) => {
        if (!group.best) return;
        const exercise = (byName[group.name] ||= { name: group.name, thumbnailUrl: null, isLoaded: false, entries: [] });
        exercise.thumbnailUrl = exercise.thumbnailUrl || group.thumbnailUrl;
        exercise.isLoaded = exercise.isLoaded || group.isLoaded;
        exercise.entries.push({
          log,
          date: logDate(log),
          group,
          record: recordsByLog?.[log.id]?.[group.name] || null,
          oneRM: group.top1RM ? epley1RM(toKg(group.top1RM.weight, group.top1RM.unit), group.top1RM.reps) : 0,
        });
      });
    });
  return Object.values(byName)
    .map((exercise) => ({
      ...exercise,
      entries: exercise.entries.map((entry) => ({
        ...entry,
        // A loaded exercise follows the load; a session of it done without load has none to show
        value: exercise.isLoaded ? toKg(entry.group.best.weight, entry.group.best.unit) : entry.group.best.reps,
      })),
    }))
    .sort((a, b) => b.entries[b.entries.length - 1].date - a.entries[a.entries.length - 1].date || a.name.localeCompare(b.name));
};

/**
 * An exercise's progress: the sessions its line draws (those with a value), the best of them (ties go to the
 * later one with more reps), how much it went up since the first, its best estimated 1RM and its latest record.
 */
export const summarizeExercise = (exercise) => {
  const points = exercise.entries.filter((entry) => entry.value > 0);
  const first = points[0] || exercise.entries[0];
  const last = exercise.entries[exercise.entries.length - 1];
  const best = points.reduce(
    (top, entry) => (entry.value > top.value || (entry.value === top.value && entry.group.best.reps >= top.group.best.reps) ? entry : top),
    first
  );
  const lastRecord = [...exercise.entries].reverse().find((entry) => entry.record) || null;
  return {
    points,
    first,
    last,
    best,
    gain: points.length > 1 ? best.value - first.value : 0,
    oneRM: Math.max(0, ...exercise.entries.map((entry) => entry.oneRM)),
    lastRecord,
  };
};

const weekStats = (start, logs) => {
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
  const inWeek = logs.filter((log) => {
    const date = logDate(log);
    return date >= start && date < end;
  });
  return {
    start,
    end,
    logs: inWeek,
    count: inWeek.length,
    duration: inWeek.reduce((total, log) => total + (log.attributes?.duration || 0), 0),
    volume: inWeek.reduce((total, log) => total + computeSessionVolume(log.attributes?.exercises || []), 0),
  };
};

const addWeeks = (date, weeks) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + weeks * 7);

// The last `count` weeks, oldest first, this one last: the 12-week chart
export const lastWeeks = (logs, count, now = new Date()) => {
  const { start } = getWeekBounds(now);
  return Array.from({ length: count }, (_, index) => weekStats(addWeeks(start, index - (count - 1)), logs || []));
};

/**
 * The feed's weeks, this one first, back to the week of the first logged session (each with its sessions newest
 * first). A run of weeks without any session folds into one entry, { start, end, count: 0, weeks: n }, so a break
 * of months takes one quiet line instead of a line a week; this week and last week always stand alone.
 */
export const historyWeeks = (logs, now = new Date()) => {
  const all = logs || [];
  const { start: thisWeek } = getWeekBounds(now);
  if (!all.length) return [weekStats(thisWeek, all)];
  const oldest = all.reduce((min, log) => (logDate(log) < min ? logDate(log) : min), logDate(all[0]));
  const firstWeek = getWeekBounds(oldest).start;
  const result = [];
  for (let index = 0; addWeeks(thisWeek, -index) >= firstWeek; index += 1) {
    const week = { ...weekStats(addWeeks(thisWeek, -index), all), ago: index };
    week.logs.sort((a, b) => logDate(b) - logDate(a));
    const previous = result[result.length - 1];
    if (!week.count && index > 1 && previous && !previous.count && previous.ago > 1) {
      // Older empty weeks join the run before them (which, going back in time, is the later one)
      previous.start = week.start;
      previous.weeks += 1;
    } else {
      result.push({ ...week, weeks: 1 });
    }
  }
  return result;
};

/**
 * The weeks the feed shows: whole weeks, newest first, until at least `count` sessions are on screen, so a week
 * is never cut in two. hasMore says whether an older week still has sessions.
 */
export const visibleWeeks = (weeks, count) => {
  const shown = [];
  let sessions = 0;
  for (const week of weeks) {
    if (sessions >= count) break;
    shown.push(week);
    sessions += week.count;
  }
  return { weeks: shown, hasMore: weeks.slice(shown.length).some((week) => week.count > 0) };
};

/**
 * What a session was called when it was done. Sessions saved since this release keep their workout's name and
 * description (saved with the session's exercises, as the API keeps no copy); older ones, and the Alexa's, fall
 * back to the workout as it's called now, or to no name when it was deleted.
 */
export const sessionWorkout = (log, workouts) => {
  const saved = (log?.attributes?.exercises || []).find((exercise) => exercise?.workoutName);
  if (saved) return { name: saved.workoutName, description: saved.workoutDescription || '' };
  const current = (workouts || []).find((workout) => workout.id === log?.attributes?.workout?.data?.id);
  return { name: current?.attributes?.name || '', description: current?.attributes?.description || '' };
};
